<template>
  <div class="flex flex-col gap-2 text-left" :data-testid="`${testidPrefix}-sheet-${leg}`">
    <div
      v-if="isLoading"
      class="min-h-[300px] w-full animate-pulse rounded-lg bg-lightGray30"
      :data-testid="`${testidPrefix}-sheet-${leg}-loading`"
    />

    <div
      v-else-if="loadError"
      class="flex min-h-[240px] w-full flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 p-6 text-center"
      :data-testid="`${testidPrefix}-sheet-${leg}-error`"
      role="alert"
    >
      <svg class="h-10 w-10 text-negative" viewBox="0 0 24 24" aria-hidden="true">
        <path :d="mdiAlertCircleOutline" fill="currentColor" />
      </svg>
      <p class="m-0 text-sm text-textPrimary">{{ loadError }}</p>
      <OutlineButton
        text="Tentar novamente"
        width="200px"
        :data-testid="`${testidPrefix}-sheet-${leg}-retry-button`"
        @click="load"
      />
    </div>

    <template v-else-if="fileUrl">
      <StepPreview
        :file="null"
        :file-url="fileUrl"
        :file-type="fileType"
        :rotation="rotation"
        :zoom="zoom"
        mode="preview"
        :show-display-name="false"
        @update:rotation="rotation = $event"
        @update:zoom="zoom = $event"
      />
      <p class="m-0 truncate text-xs text-neutral50" :title="fileName">{{ fileName }}</p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import { mdiAlertCircleOutline } from '@mdi/js'

import Network from '@/Network'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import StepPreview from '@/components/designSystem/modals/AttendanceUploadModal/StepPreview.vue'
import type { StationLegKey } from '@/types/station-evidence.types'
import { getApiErrorMessage } from '@/utils/apiError'

const props = withDefaults(
  defineProps<{
    /** Container de conciliação que VENCEU o ranking desta perna. */
    containerId: number | null
    leg: StationLegKey
    testidPrefix?: string
  }>(),
  { testidPrefix: 'station-evidence' }
)

const isLoading = ref(false)
const loadError = ref<string | null>(null)
const fileUrl = ref<string | null>(null)
const fileType = ref<string | undefined>(undefined)
const fileName = ref('Folha de monitoramento.pdf')
// `rotation`/`zoom` do `StepPreview` são props CONTROLADAS: sem estes refs o
// botão de girar não gira.
const rotation = ref(0)
const zoom = ref(1)

/** `blob:` fica preso à aba até ser revogado — a folha pode ter dezenas de MB. */
function revoke() {
  if (fileUrl.value?.startsWith('blob:')) URL.revokeObjectURL(fileUrl.value)
  fileUrl.value = null
}

async function load() {
  const containerId = props.containerId
  if (containerId == null) return

  revoke()
  isLoading.value = true
  loadError.value = null
  rotation.value = 0
  zoom.value = 1
  try {
    // O nome do arquivo é acessório: se o detalhe falhar, o preview ainda vale.
    const [blob, detail] = await Promise.all([
      Network.dsAttendance.downloadEvidenceFile(containerId),
      Network.dsAttendance.getById(containerId).catch(() => null)
    ])
    if (containerId !== props.containerId) return
    fileUrl.value = URL.createObjectURL(blob)
    fileType.value = blob.type
    fileName.value = detail?.sheetFilename || 'Folha de monitoramento.pdf'
  } catch (error) {
    console.error('Erro ao buscar a folha de presença do plantão:', error)
    if (containerId === props.containerId) {
      loadError.value = getApiErrorMessage(error, 'Não foi possível carregar a página da folha.')
    }
  } finally {
    if (containerId === props.containerId) isLoading.value = false
  }
}

watch(
  () => props.containerId,
  () => void load(),
  { immediate: true }
)

onBeforeUnmount(revoke)
</script>
